<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import VueRecaptcha from 'vue3-recaptcha2'
import createContact from '../graphql/createContact.gql'

const config = useRuntimeConfig()
const localePath = useLocalePath()
const { t, locale } = useI18n()

defineI18nRoute({
  paths: {
    'en-CA': '/contact',
    'fr-CA': '/contactez-nous'
  }
})

useHead({
  title: t('navigation.contact.metatitle'),
  meta: [{ name: 'description', content: t('navigation.contact.metadescription') }]
})

const router = useRouter()

const name = ref('')
const email = ref('')
const subject = ref('')
const comments = ref('')
const captchaResponse = ref()
const isSubmitting = ref(false)

const recaptchaVerified = (response: string) => {
  captchaResponse.value = response
}
const recaptchaExpired = () => {
  captchaResponse.value = null
}
const recaptchaFailed = () => {}

const submitForm = async () => {
  isSubmitting.value = true

  const data = {
    Name: name.value,
    Email: email.value,
    Subject: subject.value,
    Comments: comments.value,
    publishedAt: '2000-01-01T00:00:00Z'
  }

  try {
    if (!captchaResponse.value || name.value === '' || email.value === '' || subject.value === '') {
      if (locale.value === 'en-CA') {
        alert('Please fill in all the required fields')
      } else {
        alert('Veuillez compléter tous les champs obligatoires')
      }

      isSubmitting.value = false
      return
    }

    // Send the recaptchaResponse token to your server for verification
    const response = await fetch('/api/verify-recaptcha', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ token: captchaResponse.value })
    })

    const result = await response.json()

    if (result.success) {
      const { mutate } = await useMutation(createContact, { variables: { data } })
      const _response = await mutate()

      if (locale.value === 'en-CA') {
        router.push('/information-request-confirmation')
      } else {
        router.push('/fr-CA/confirmation-demande-information')
      }
    }
  } catch (error) {
    console.error('Error: ', error)
  }

  isSubmitting.value = false
}
</script>

<template>
  <div class="wrapContent">
    <div class="entetePage">
      <div class="wrapper">
        <div class="filAriane">
          <span class="">
            <NuxtLink :to="localePath('index')">{{ $t('navigation.home.name') }}</NuxtLink>
            <span class=""> &gt; </span>
            <span class="">{{ $t('navigation.contact.name') }}</span>
          </span>
        </div>
        <div class="title">
          <h1><span class="">{{ $t('navigation.contact.name') }}</span></h1>
        </div>
      </div>
    </div>
    <div class="contentPage">
      <div class="wrapper clearfix">
        <MenuContact />
        <div class="contenu right">
          <p>{{ t('contact.intro.text') }}</p>
          <p>* {{ t('global.forms.requiredFields') }}</p>
          <form>
            <label for="name">{{ t('global.forms.name') }} *</label><br>
            <input id="name"
                   v-model="name"
                   type="text"
                   name="name"
                   required><br>
            <label for="email">{{ t('global.forms.email') }} *</label><br>
            <input id="email"
                   v-model="email"
                   type="email"
                   name="email"
                   required><br>
            <label for="subject">{{ t('global.forms.object') }} *</label><br>
            <input id="subject"
                   v-model="subject"
                   type="text"
                   name="subject"
                   required><br>
            <label for="comments">{{ t('global.forms.comments') }}</label><br>
            <textarea id="comments"
                      v-model="comments"
                      name="comments"
                      cols="30"
                      rows="4" /><br><br>
            <VueRecaptcha ref="vueRecaptcha"
              :sitekey="config.public.recaptchaKey"
              size="normal"
              theme="light"
              @verify="recaptchaVerified"
              @expire="recaptchaExpired"
              @fail="recaptchaFailed"
            />
            <br>
            <input type="submit"
                   :value="t('global.forms.submit')"
                   class="form-submit"
                   :disabled="isSubmitting"
                   @click.prevent="() => submitForm()">
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss"></style>
